import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, AlertTriangle, ArrowRight } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import api from "@/lib/api";

const COVERAGE_GREEN = [
    "Amazon, Uber, SNCF Connect",
    "Apple, Google, Microsoft",
    "OVH Cloud, Notion, LinkedIn",
    "Booking, Airbnb, Air France",
    "Carrefour Drive, Fnac.com",
    "Total Energies, Stripe, PayPal",
    "Restaurants connectés (Sunday, Yavin)",
];

const COVERAGE_AMBER = [
    "Petit restaurant / bistrot papier",
    "Bar, café, boulangerie traditionnel",
    "Pharmacie de quartier",
    "Paiement en espèces",
];

export default function Honnetete() {
    const [stats, setStats] = useState({ auto: 75, manual: 25 });

    // tiny detail: try to pull recent automation rate to give a "honest" data point
    useEffect(() => {
        let cancelled = false;
        api.get("/")
            .then(() => {
                if (cancelled) return;
                // No public endpoint — keep defaults
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="min-h-screen">
            <PublicHeader />

            {/* Hero */}
            <section className="pt-32 pb-12 sm:pt-44 sm:pb-16 px-6 sm:px-8">
                <div className="max-w-3xl mx-auto">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3 tk-rise">
                        Honnêteté technique
                    </p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1D1D1F] tk-rise tk-rise-1">
                        Ce que l&apos;on automatise,<br />et ce que l&apos;on ne peut pas.
                    </h1>
                    <p className="mt-6 text-lg text-[#86868B] leading-relaxed tk-rise tk-rise-2">
                        Aucune API bancaire au monde ne transmet le détail des tickets de caisse — seulement le
                        montant payé. Voici donc la vérité brute&nbsp;: ce qu&apos;on automatise à 100 %, et là où il
                        faut 7 secondes de photo.
                    </p>
                </div>
            </section>

            {/* Two columns */}
            <section className="px-6 sm:px-8 pb-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
                    <div className="tk-card p-8 hover:-translate-y-1 transition-all tk-rise tk-rise-3" data-testid="coverage-auto">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-11 h-11 rounded-2xl bg-[#34C759]/15 flex items-center justify-center">
                                <Check className="w-5 h-5 text-[#34C759]" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-[#34C759]">
                                    100 % auto · ~{stats.auto} % des paiements pro
                                </p>
                                <h3 className="text-lg font-semibold text-[#1D1D1F]">
                                    Justificatif complet sans rien faire
                                </h3>
                            </div>
                        </div>
                        <p className="text-sm text-[#86868B] mb-5 leading-relaxed">
                            Marchands qui envoient un e-reçu après paiement carte. Ticketify le récupère depuis votre
                            boîte mail, le décode et l&apos;attache à la transaction bancaire.
                        </p>
                        <ul className="space-y-2.5">
                            {COVERAGE_GREEN.map((m) => (
                                <li key={m} className="text-sm text-[#1D1D1F] flex items-start gap-2">
                                    <Check className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="tk-card p-8 hover:-translate-y-1 transition-all tk-rise tk-rise-4" data-testid="coverage-photo">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-11 h-11 rounded-2xl bg-[#FF9F0A]/15 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-[#FF9F0A]" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-[#FF9F0A]">
                                    7 sec de photo · ~{stats.manual} % des paiements pro
                                </p>
                                <h3 className="text-lg font-semibold text-[#1D1D1F]">Le ticket papier reste papier</h3>
                            </div>
                        </div>
                        <p className="text-sm text-[#86868B] mb-5 leading-relaxed">
                            Petits commerces sans système connecté = ticket uniquement imprimé. Notre IA OCR (GPT-5.2
                            vision) extrait tout en 5 secondes depuis une photo&nbsp;: articles, TVA, SIRET, n° de facture.
                        </p>
                        <ul className="space-y-2.5">
                            {COVERAGE_AMBER.map((m) => (
                                <li key={m} className="text-sm text-[#1D1D1F] flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-[#FF9F0A] mt-0.5 flex-shrink-0" />
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto mt-12">
                    <div className="p-8 rounded-3xl bg-[#1D1D1F] tk-cta-dark text-white">
                        <p className="text-sm leading-relaxed">
                            <strong className="text-white">Pourquoi pas 100 % auto ?</strong> &nbsp;Parce que les APIs
                            bancaires (Revolut, Bridge, Tink, Plaid…) ne transmettent <em>jamais</em> le contenu détaillé
                            du ticket — seulement le marchand et le montant. C&apos;est une limite physique du réseau
                            Visa/Mastercard, pas un manque de Ticketify.{" "}
                            <Link to="/comment-ca-marche" className="underline hover:opacity-80">
                                En savoir plus →
                            </Link>
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 sm:px-8 pb-24">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                        Prêt à voir ça en vrai&nbsp;?
                    </h2>
                    <p className="mt-3 text-[#86868B]">5 minutes pour tout brancher. Le reste se passe tout seul.</p>
                    <Link
                        to="/register"
                        className="tk-btn tk-btn-primary mt-6 inline-flex"
                        data-testid="honnetete-cta"
                    >
                        Créer mon compte <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <footer className="border-t border-black/5 py-10 px-6 sm:px-8 text-center text-sm text-[#86868B]">
                © 2026 Ticketify
            </footer>
        </div>
    );
}
